import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import style from './CareerCompanion.module.css';
import MapExplorer from './MapExplorer';
import Spinner from './Spinner';
import { useUserContext } from '../../contexts/UserContext';
import { useCV } from '../../contexts/CVContext';
// Simple SVG Circular Progress Component
const CircularProgress = ({ value, size = 120, strokeWidth = 10, color = '#432C5B' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;

    return (
        <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="#E6E6E6"
                    strokeWidth={strokeWidth}
                    fill="none"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                />
            </svg>
            <div style={{ position: 'absolute', fontSize: '24px', fontWeight: 'bold', color: color }}>
                {value ?? 0}
            </div>
        </div>
    );
};

const CareerCompanionPage = () => {
    const navigate = useNavigate();
    const { user } = useUserContext();
    const { isCVUploaded } = useCV();
    const userId = user?.user?.user_id;
    const [selectedTab, setSelectedTab] = useState('CV Report');

    // Data State
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Gate: user must click "Generate Analysis" before any backend call
    const [hasStarted, setHasStarted] = useState(false);

    // Job search state (lifted so it persists across tab changes)
    const [selectedJobRole, setSelectedJobRole] = useState(null);
    const [jobLocation, setJobLocation] = useState('San Francisco, CA');
    const [jobCompanies, setJobCompanies] = useState([]);

    const API_URL = `${process.env.REACT_APP_API_URL || ''}/chats`;

    useEffect(() => {
        if (!hasStarted) return;

        if (!userId || userId === 'guest' || userId === 'current') {
            setLoading(false);
            return;
        }

        if (!isCVUploaded) {
            setLoading(false);
            navigate('/dashboard/upload');
            return;
        }

        // Check for cached data first (sessionStorage: tab-scoped, not sent to server)
        const cacheKey = `cc_analysis_${userId}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                setAnalysis(parsed);
                setError(null);
                setLoading(false);
                return;
            } catch (e) {
                sessionStorage.removeItem(cacheKey);
            }
        }

        setLoading(true);

        fetch(`${API_URL}/analyze`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId })
        })
            .then(async response => {
                if (!response.ok) {
                    const text = await response.text();
                    let detail = '';
                    try {
                        const parsed = JSON.parse(text);
                        detail = parsed.detail || '';
                    } catch {
                        detail = text;
                    }

                    if (
                        response.status === 404 &&
                        typeof detail === 'string' &&
                        detail.startsWith('No file found in master.files')
                    ) {
                        navigate('/dashboard/upload');
                        throw new Error('Redirecting to upload page');
                    }

                    throw new Error(detail || 'Failed to fetch analysis');
                }
                return response.json();
            })
            .then(data => {
                setAnalysis(data);
                setError(null);
                sessionStorage.setItem(`cc_analysis_${userId}`, JSON.stringify(data));
            })
            .catch(err => {
                if (err.message === 'Redirecting to upload page') return;
                console.error("Fetch Error:", err);
                setError(err.message);
            })
            .finally(() => setLoading(false));
    }, [hasStarted, userId, API_URL, navigate, isCVUploaded]);

    // When analysis arrives, set default role & location (only once)
    useEffect(() => {
        if (!analysis) return;

        // default role: first suggested profile
        if (!selectedJobRole && analysis.suggested_profiles && analysis.suggested_profiles.length > 0) {
            setSelectedJobRole(analysis.suggested_profiles[0]);
        }

        // default location: candidate_location if present
        if (analysis.candidate_location && (!jobLocation || jobLocation === 'San Francisco, CA')) {
            setJobLocation(analysis.candidate_location);
        }
    }, [analysis]); // intentionally not depending on selectedJobRole/jobLocation to avoid re-running

    const handleUseMock = () => {
        const mock = {
            score: 85,
            candidate_location: "New York, NY",
            skills: ["React", "Python", "Data Analysis", "UI/UX"],
            interests: ["Artificial Intelligence", "Web Development"],
            summary: "Experienced developer with a strong background in frontend technologies and a growing interest in AI integrations.",
            suggested_profiles: ["Frontend Engineer", "Full Stack Developer"],
            detailed_metrics: {
                "Technical Proficiency": 90,
                "Soft Skills": 80,
                "Experience Impact": 85,
                "Education/Certifications": 75,
                "Presentation": 88
            },
            agentic_advice: [
                "Consider contributing to open source AI projects to strengthen your portfolio.",
                "Your React skills are strong; focus on Next.js for full-stack capabilities.",
                "Highlight concrete achievements (metrics, outcomes) under each role."
            ]
        };
        setAnalysis(mock);
        setSelectedJobRole(mock.suggested_profiles[0]);
        setJobLocation(mock.candidate_location);
        setError(null);
    };

    // ------------------- LANDING SCREEN -------------------

    const renderLanding = () => (
        <div className={style.landingScreen}>
            <div className={style.landingIconWrap}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 13H8" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17H8" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 9H9H8" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
            <h1 className={style.landingTitle}>CV Analyzer</h1>
            <p className={style.landingSubtitle}>
                Get an AI-powered breakdown of your CV skills, strengths, improvement areas, and role recommendations tailored to you.
            </p>
            {userId && userId !== 'guest' && userId !== 'current' ? (
                <>
                    <button
                        className={style.generateButton}
                        onClick={() => setHasStarted(true)}
                    >
                        Generate Analysis
                        <span className={style.generateButtonArrow}>→</span>
                    </button>
                    <p className={style.landingNote}>Analysis typically takes 15–30 seconds</p>
                </>
            ) : (
                <p className={style.landingNoUser}>
                    No profile linked. Please navigate here from your dashboard.
                </p>
            )}
        </div>
    );

    // ------------------- RENDER SECTIONS -------------------

    const getGrade = (score) => {
        if (score >= 90) return 'A'
        if (score >= 80) return 'B+'
        if (score >= 70) return 'B'
        if (score >= 60) return 'C+'
        return 'C'
    }

    const getScoreColor = (score) => {
        if (score >= 80) return '#22c55e'
        if (score >= 60) return '#f59e0b'
        return '#ef4444'
    }

    const SKILL_COLORS = [
        { bg: 'rgba(67,44,91,0.10)', color: '#432C5B' },
        { bg: 'rgba(52,152,219,0.10)', color: '#2471a3' },
        { bg: 'rgba(39,174,96,0.10)', color: '#1e8449' },
        { bg: 'rgba(142,68,173,0.10)', color: '#7d3c98' },
        { bg: 'rgba(230,126,34,0.10)', color: '#ca6f1e' },
    ]

    const getMetricColor = (val) => {
        if (val >= 85) return 'linear-gradient(90deg, #22c55e, #16a34a)'
        if (val >= 70) return 'linear-gradient(90deg, #432C5B, #61487c)'
        if (val >= 55) return 'linear-gradient(90deg, #f59e0b, #d97706)'
        return 'linear-gradient(90deg, #ef4444, #dc2626)'
    }

    const renderReport = () => (
        <div className={style.section}>
            {loading && <Spinner size="large" message="Analyzing your CV..." />}

            {error && (
                <div className={style.alert}>
                    <strong>Backend Connection Failed</strong>
                    <p style={{ fontSize: '0.9em', margin: '5px 0' }}>
                        {error}. <br />
                        Ensure the backend is running.
                    </p>
                    {process.env.NODE_ENV === 'development' && (
                        <button
                            onClick={handleUseMock}
                            className={style.button}
                            style={{ marginTop: '10px', fontSize: '12px', padding: '5px 10px' }}
                        >
                            Use Demo Data
                        </button>
                    )}
                </div>
            )}

            {!loading && !error && analysis && (
                <>
                    {/* Hero band: score + summary + roles */}
                    <div className={style.heroSection}>
                        <div className={style.heroLeft}>
                            <CircularProgress
                                value={analysis.score || 0}
                                size={140}
                                color={getScoreColor(analysis.score || 0)}
                            />
                            <span className={style.scoreGrade}>{getGrade(analysis.score || 0)}</span>
                            <span className={style.scoreLabel}>CV Score</span>
                        </div>
                        <div className={style.heroDivider} />
                        <div className={style.heroRight}>
                            {analysis.candidate_location && (
                                <div className={style.locationPill}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 21C16 17 20 13 20 9a8 8 0 10-16 0c0 4 4 8 8 12z" /><circle cx="12" cy="9" r="3" /></svg>
                                    {analysis.candidate_location}
                                </div>
                            )}
                            <p className={style.heroSummary}>{analysis.summary}</p>
                            {analysis.suggested_profiles && analysis.suggested_profiles.length > 0 && (
                                <div className={style.rolesRow}>
                                    <span className={style.rolesLabel}>Suggested roles</span>
                                    {analysis.suggested_profiles.map((role, i) => (
                                        <span key={i} className={style.roleChip}>{role}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Skills + Metrics side by side */}
                    <div className={style.twoColGrid}>
                        <div className={style.reportCard}>
                            <h3 className={style.cardTitle}>Skills Detected</h3>
                            <div className={style.skillsWrap}>
                                {analysis.skills?.map((skill, idx) => {
                                    const c = SKILL_COLORS[idx % SKILL_COLORS.length]
                                    return (
                                        <span key={idx} className={style.skillChip} style={{ background: c.bg, color: c.color }}>
                                            {skill}
                                        </span>
                                    )
                                })}
                            </div>
                        </div>

                        {analysis.detailed_metrics && (
                            <div className={style.reportCard}>
                                <h3 className={style.cardTitle}>CV Dimensions</h3>
                                <div className={style.metricsGroup}>
                                    {Object.entries(analysis.detailed_metrics).map(([key, val]) => (
                                        <div key={key} className={style.metricRow}>
                                            <div className={style.metricRowLabel}>{key}</div>
                                            <div className={style.metricRowTrack}>
                                                <div
                                                    className={style.metricRowFill}
                                                    style={{ width: `${val}%`, background: getMetricColor(val) }}
                                                />
                                            </div>
                                            <div className={style.metricRowValue}>{val}%</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Advice cards */}
                    {analysis.agentic_advice && analysis.agentic_advice.length > 0 && (
                        <div className={style.reportCard} style={{ marginTop: '20px' }}>
                            <h3 className={style.cardTitle}>Chimp's Advice</h3>
                            <div className={style.adviceGrid}>
                                {analysis.agentic_advice.map((advice, i) => (
                                    <div key={i} className={style.adviceCard}>
                                        <span className={style.adviceNum}>{i + 1}</span>
                                        <p className={style.adviceText}>{advice}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className={style.pageContainer}>
            <div className={style.glassCard}>
                {!hasStarted ? (
                    renderLanding()
                ) : (
                    <>
                        <div className={style.navTabs}>
                            {['CV Report', 'Find Companies Near You'].map(tab => (
                                <button
                                    key={tab}
                                    className={`${style.tabButton} ${selectedTab === tab ? style.activeTab : ''}`}
                                    onClick={() => setSelectedTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className={style.contentArea}>
                            {selectedTab === 'CV Report' && renderReport()}
                            {selectedTab === 'Find Companies Near You' && (
                                <MapExplorer
                                    jobProfile={selectedJobRole}
                                    setJobProfile={setSelectedJobRole}
                                    availableRoles={analysis?.suggested_profiles || []}
                                    location={jobLocation}
                                    setLocation={setJobLocation}
                                    companies={jobCompanies}
                                    setCompanies={setJobCompanies}
                                    apiUrl={API_URL}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CareerCompanionPage;
